<template>
  <v-dialog
    id="defect-info-dialog"
    v-model="dialog"
    v-bind="defectDialogViewport"
    open-on-focus
    @click:outside="close"
    @keydown.esc="close"
  >
    <v-carousel
      :show-arrows="false"
      hide-delimiter-background
      delimiter-icon="mdi-circle"
      height="100%"
      class="dialog-edge-navigation-carousel"
    >
      <v-carousel-item>
        <DialogEdgeNavigation
          :disable-tooltips="!dialog"
          :prev-disabled="!canNavigatePrevious"
          :next-disabled="!canNavigateNext"
          :prev-tooltip-i18n-key="edgeNavPrevTooltipKey"
          :next-tooltip-i18n-key="edgeNavNextTooltipKey"
          :keyboard-active="false"
          @prev="navigateToPrevious"
          @next="navigateToNext"
        />
      </v-carousel-item>
    </v-carousel>

    <v-card
      v-if="showList"
      flat
      outlined
      class="defect-info-dialog-card defect-info-dialog-shell rounded"
    >
      <CrudFormDialogHeader
        :kicker="defectHeaderKicker"
        :title="$t('defects.dialogListTitle')"
        show-close
        @close="close"
      >
        <template slot="append">
          <v-tooltip bottom>
            <template slot="activator" slot-scope="{ on, attrs }">
              <v-btn
                outlined
                class="solvit-crud-outline-btn solvit-crud-outline-btn--primary defect-info-header-action-btn"
                v-bind="attrs"
                v-on="on"
                @click="toggleList(null, false)"
              >
                <v-icon left> mdi-table-plus </v-icon>
                {{ $t('defects.registerShort') }}
              </v-btn>
            </template>
            <span>{{ $t('defects.registerDefect') }} (Alt+Q)</span>
          </v-tooltip>
        </template>
      </CrudFormDialogHeader>

      <v-card-text class="defect-info-dialog-list-body pa-0 px-3 pt-2 pb-3">
        <v-sheet outlined rounded class="defect-info-list-surround pa-0">
          <v-list v-if="defectList.length <= 0" class="py-0" dense>
            <v-subheader class="text--secondary">{{ $t('defects.noDefectsFound') }}</v-subheader>

            <v-list-item tabindex="0" @click="toggleList(null, false)">
              <v-list-item-avatar class="align-self-start">
                <v-icon color="primary"> mdi-table-plus </v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ $t('defects.registerDefect') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-list
            v-if="defectList.length > 0"
            subheader
            two-line
            dense
            class="py-0 defect-info-defect-list"
            role="listbox"
            :aria-label="$t('defects.dialogListTitle')"
          >
            <v-subheader class="text--secondary text-caption">{{
              $t('defects.listHint')
            }}</v-subheader>
            <v-list-item
              v-for="(defect, index) of defectList"
              :key="defect && defect.id ? defect.id : index"
              :ref="`defect-item-${index}`"
              tabindex="0"
              class="defect-list-item"
              :class="{ 'focused-item': focusedListItemIndex === index }"
              role="option"
              :aria-selected="focusedListItemIndex === index"
              :aria-label="$t('defects.selectAria')"
              @click="onDefectListRowClick(defect, index)"
              @focus="focusedListItemIndex = index"
            >
              <v-list-item-avatar class="align-self-start">
                <v-icon color="warning"> mdi-alert-circle </v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ $t('defects.defectIdLine', { id: defect && defect.id ? defect.id : 'N/A' }) }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ defect?.createTime ? defect.createTime.substring(0, 10) : '' }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  <span>{{ defect && defect.defectCode ? defect.defectCode.name : '-' }}</span>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  <span>{{
                    defect && defect.mlDefectConfirmed
                      ? defect.mlDefectConfirmed === 'YES'
                        ? $t('defects.aiConfirmed')
                        : $t('defects.aiUnconfirmed')
                      : $t('defects.userDefinedMl')
                  }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-sheet>
      </v-card-text>
    </v-card>
    <v-card
      v-if="!showList"
      flat
      outlined
      class="defect-info-dialog-card defect-info-dialog-shell rounded"
    >
      <CrudFormDialogHeader
        :kicker="defectHeaderKicker"
        :title="defectDetailHeaderTitle"
        show-close
        @close="close"
      >
        <template slot="append">
          <v-tooltip bottom>
            <template slot="activator" slot-scope="{ on, attrs }">
              <v-btn
                outlined
                class="solvit-crud-outline-btn defect-info-header-action-btn"
                v-bind="attrs"
                v-on="on"
                @click="toggleList(null, true)"
              >
                <v-icon left> mdi-view-list </v-icon>
                {{ $t('defects.defectList') }}
              </v-btn>
            </template>
            <span>{{ $t('defects.viewList') }} (Alt+W)</span>
          </v-tooltip>
        </template>
      </CrudFormDialogHeader>
      <DefectInfoCard
        ref="defectInfoCard"
        :reset-flag="resetFlag"
        :reset-options="resetFormOptions"
      />
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapGetters, mapActions, mapMutations } from 'vuex';
  import { moduleDefectApi } from '../../apis/module.defect.api';
  import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
  import DialogEdgeNavigation from '@/components/common/DialogEdgeNavigation.vue';
  import { MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';

  export default {
    name: 'DefectInfoDialog',
    components: {
      CrudFormDialogHeader,
      DialogEdgeNavigation,
      DefectInfoCard: () => import('../card/DefectInfoCard.vue')
    },
    data: () => ({
      menuBarTitle: null,
      focusedListItemIndex: 0,
      isLoadingDefects: false,
      isInitializing: false,
      resetFlag: false,
      /** resetFlag 토글 시 DefectInfoCard.resetForm 에 전달 (예: 열화상 네비 후 온도 유지) */
      resetFormOptions: {}
    }),
    computed: {
      ...mapGetters({
        defect: 'defect/getDefect',
        thermalImage: 'defect/getThermalImage',
        defectTemperature: 'defect/getDefectTemperature',
        showList: 'defect/getShowList',
        isDefectSelected: 'defect/getIsDefectSelected',
        isEdit: 'defect/getIsEdit',
        solarThermalImageId: 'defect/getSolarThermalImageId',
        defectNavigationMode: 'defect/getDefectNavigationMode',
        canNavigateModuleDefectPrevious: 'defect/canNavigateModuleDefectPrevious',
        canNavigateModuleDefectNext: 'defect/canNavigateModuleDefectNext',
        dashboardCanNavigatePrevious: 'dashboard/canNavigatePrevious',
        dashboardCanNavigateNext: 'dashboard/canNavigateNext',
        selectedThermalImage: 'dashboard/getSelectedThermalImage',
        selectedThermalImageIndex: 'dashboard/getSelectedThermalImageIndex',
        dashboardThermalImages: 'dashboard/getThermalImages',
        shouldRenewDefectList: 'defect/shouldRenewDefectList',
        defectList: 'defect/getDefectList',
        focusDefectListItemId: 'defect/getFocusDefectListItemId'
      }),
      dialog: {
        get() {
          return this.$store.getters['defect/defectInfoDialogOpen'];
        },
        set(value) {
          if (!value) {
            this.closeDefectInfoDialog();
          }
        }
      },
      dialogData() {
        const data = this.$store.getters['defect/defectInfoDialogData'];
        return data;
      },
      defectTypeInfo() {
        return this.defect?.defectTypeInfo;
      },
      mainDefectTypes() {
        return this.defectTypeInfo?.options.mainOptions || [];
      },
      subDefectTypes() {
        return this.defectTypeInfo?.options.subOptions || [];
      },
      mainDefectCode: {
        get() {
          return this.defectTypeInfo?.selected.selectedMainType || this.mainDefectTypes[1];
        },
        set(val) {
          this.updateSelectedMainType(val);
        }
      },
      subDefectCode: {
        get() {
          return this.defectTypeInfo?.selected.selectedSubDefectType;
        },
        set(val) {
          this.SET_SELECTED_SUB_DEFECT_TYPE(val);
        }
      },
      isDefectSelectedFromMap() {
        return this.isDefectSelected && this.defect && this.defect.id;
      },
      isDialogOpen() {
        return this.dialog;
      },
      defectId() {
        return this.defect?.id;
      },
      defectStatus: {
        get() {
          console.log('[DEFECT INFO DIALOG] defectStatus get:', this.defect?.defectStatus);
          return this.defect?.defectStatus || 'FOUND';
        },
        set(val) {
          console.log('[DEFECT INFO DIALOG] defectStatus set:', val);
          this.UPDATE_DEFECT_STATUS(val);
        }
      },
      defectDesc: {
        get() {
          console.log(
            '[DEFECT INFO DIALOG] defectDesc get:',
            this.localDefectDesc || this.defect?.defectDesc
          );
          return this.localDefectDesc || this.defect?.defectDesc;
        },
        set(val) {
          console.log('[DEFECT INFO DIALOG] defectDesc set:', val);
          this.UPDATE_DEFECT_DESC(val);
        }
      },
      mlDefectConfirmed: {
        get() {
          return this.defect?.mlDefectConfirmed;
        },
        set(val) {
          this.updateMlDefectConfirmed(val);
        }
      },
      // thermalImage 변경 감지용 computed
      currentThermalImageId() {
        return this.thermalImage?.id;
      },
      /** modalLayout: 상한은 공통 토큰, 너비는 결함 다이얼로그 예외 비율 유지. 상세는 min-height 미지정으로 불필요한 세로 확장 방지 */
      defectDialogViewport() {
        return {
          ...MODAL_P2_FORM_VIEWPORT,
          maxWidth: this.showList ? '50%' : '77%',
          minWidth: this.showList ? '50%' : '77%',
          minHeight: this.showList ? 'min(480px, 70vh)' : undefined,
          contentClass: 'solvit-dialog-form-shell defect-info-dialog-shell'
        };
      },
      defectHeaderKicker() {
        return this.menuBarTitle || '—';
      },
      isModuleNavigationMode() {
        return this.defectNavigationMode === 'module';
      },
      canNavigatePrevious() {
        return this.isModuleNavigationMode
          ? this.canNavigateModuleDefectPrevious
          : this.dashboardCanNavigatePrevious;
      },
      canNavigateNext() {
        return this.isModuleNavigationMode
          ? this.canNavigateModuleDefectNext
          : this.dashboardCanNavigateNext;
      },
      edgeNavPrevTooltipKey() {
        return this.isModuleNavigationMode
          ? 'defects.previousModuleDefect'
          : 'defects.previousImage';
      },
      edgeNavNextTooltipKey() {
        return this.isModuleNavigationMode ? 'defects.nextModuleDefect' : 'defects.nextImage';
      },
      defectDetailHeaderTitle() {
        const mode =
          this.defect && this.defect.id ? this.$t('defects.modeEdit') : this.$t('defects.modeAdd');
        return `${this.$t('defects.infoTitle')} · ${mode}`;
      }
    },
    methods: {
      ...mapActions({
        navigateToNewDefectDialog: 'dashboard/navigateToNewDefectDialog',
        closeDefectInfoDialog: 'defect/closeDefectInfoDialog'
      }),
      ...mapMutations('defect', [
        'UPDATE_SHOW_LIST',
        'UPDATE_DEFECT_STATUS',
        'UPDATE_DEFECT_DESC',
        'updateMlDefectConfirmed',
        'SET_DEFECT',
        'SET_DEFECT_MAIN_TYPE',
        'SET_DEFECT_SUB_TYPE'
      ]),
      close() {
        // form validation 초기화
        this.resetForm();
        this.closeDefectInfoDialog();
      },
      resetForm(options = {}) {
        this.resetFormOptions = {
          preserveDefectTemperature: false,
          ...options
        };
        this.resetFlag = true;
        this.$nextTick(() => {
          this.resetFlag = false;
          this.resetFormOptions = {};
        });
      },
      async initialize(solarThermalImageId) {
        if (this.isInitializing) return;

        this.isInitializing = true;
        try {
          await this.loadDefects(solarThermalImageId);
        } finally {
          this.isInitializing = false;
        }
      },
      async loadDefects(solarThermalImageId) {
        if (this.isLoadingDefects) return;

        this.isLoadingDefects = true;
        try {
          // dashboard store의 showAiDefectFlag를 사용하여 동기화 보장
          const aiInclude = this.$store.state.dashboard?.showAiDefectFlag || false;
          console.log('[DEFECT INFO DIALOG] loadDefects - aiInclude:', aiInclude);
          const response = await moduleDefectApi.getDefectsByThermalImageId(
            solarThermalImageId,
            aiInclude
          );
          const raw = response?.content || [];
          const thermalImage =
            this.resolveThermalImageForOpenDefectList() ||
            this.$store.getters['dashboard/getSelectedThermalImage'];
          this.localDefects = this.filterDefectsForDisplay(raw, thermalImage);
        } catch (error) {
          console.error('[DEFECT INFO DIALOG] 결함 목록 로드 실패:', error);
          this.localDefects = [];
        } finally {
          this.isLoadingDefects = false;
        }
      },
      async toggleList(defect, showList) {
        console.log('[DEFECT INFO DIALOG] toggleList 호출:', { defect, showList });

        // 전략 패턴으로 동작 결정 (데이터 로드 후 store가 폼 초기화 — 선행 resetForm 시 결함유형 목록 비음)
        const actionStrategy = this.getActionStrategy(defect, showList);
        await actionStrategy.execute();
      },

      /**
       * 결함 목록 표시 — 다이얼로그에서 이전/다음 장으로 네비해도, 수정 중인 결함이 속한 열화상 기준으로 목록을 연다.
       */
      async showDefectList() {
        console.log('[DEFECT INFO DIALOG] 결함 목록 표시');
        const thermalImage = this.resolveThermalImageForOpenDefectList();
        if (!thermalImage || thermalImage.id == null || thermalImage.id === '') {
          console.warn('[DEFECT INFO DIALOG] 결함 목록용 열화상을 찾지 못함');
          return;
        }
        const focusDefectId =
          this.defect?.id != null && this.defect.id !== '' ? Number(this.defect.id) : null;
        this.$store.dispatch('defect/openDefectList', {
          thermalImage,
          focusDefectId
        });
      },

      resolveThermalImageForOpenDefectList() {
        const nested = this.defect?.solarThermalImage;
        const defectThermalId =
          nested && typeof nested === 'object' && nested.id != null && nested.id !== ''
            ? nested.id
            : null;

        if (defectThermalId != null) {
          const images = this.dashboardThermalImages || [];
          const fromList = images.find(img => img && Number(img.id) === Number(defectThermalId));
          if (fromList) {
            return fromList;
          }
          return nested;
        }

        if (this.isEdit && this.solarThermalImageId != null && this.solarThermalImageId !== '') {
          const images = this.dashboardThermalImages || [];
          const fromList = images.find(
            img => img && Number(img.id) === Number(this.solarThermalImageId)
          );
          if (fromList) {
            return fromList;
          }
        }

        return this.thermalImage;
      },

      filterDefectsForDisplay(defects) {
        return Array.isArray(defects) ? defects : [];
      },

      /**
       * 결함 수정 모드
       */
      async editDefect(defect) {
        console.log('[DEFECT INFO DIALOG] 결함 수정 모드:', defect);
        await this.$store.dispatch('defect/openDefectFromList', {
          thermalImage: this.thermalImage,
          defectId: defect.id
        });
      },

      /**
       * 새 결함 추가 모드
       */
      async createNewDefect() {
        console.log('[DEFECT INFO DIALOG] 새 결함 추가 모드');
        await this.$store.dispatch('defect/openImageForNewDefect', {
          thermalImage: this.thermalImage
        });
      },
      // 제목 업데이트 함수
      updateDialogTitle() {
        if (this.thermalImage) {
          this.menuBarTitle = this.thermalImage.imgName || '-';
          console.log('[DEFECT INFO DIALOG] 제목 업데이트:', this.menuBarTitle);
        } else {
          this.menuBarTitle = '-';
          console.warn('[DEFECT INFO DIALOG] thermalImage가 없어서 기본 제목 설정');
        }
      },

      initializeDialog() {
        if (!this.thermalImage) {
          console.warn('[DEFECT INFO DIALOG] thermalImage가 없습니다.');
          return;
        }

        // 제목 업데이트
        this.updateDialogTitle();

        // 결함 목록 로드
        this.initialize(this.thermalImage.id);

        // RGB 이미지는 props로 받아옴

        this.addGlobalEventListeners();

        // 폼·결함유형은 openDefectInfoDialog → loadDialogData(세션 로더) 완료 후 store가 초기화함.
        // 선행 resetForm()은 parentTypes 로드 전에 폼을 비워 셀렉트가 빈 것처럼 보이는 경합을 만든다.
      },
      addGlobalEventListeners() {
        /* capture: 다이얼로그/캐러셀/포커스 트랩보다 먼저 받아 ↑↓·Enter가 항상 동작하도록 */
        document.addEventListener('keydown', this.handleShortcuts, true);
      },
      removeGlobalEventListeners() {
        document.removeEventListener('keydown', this.handleShortcuts, true);
      },
      /** 버튼·링크·폼 필드에서는 Enter 기본 동작 유지 */
      isKeyboardInteractiveTarget(target) {
        if (!target || typeof target.closest !== 'function') return false;
        return !!target.closest(
          'button, [role="button"], a[href], input, textarea, select, [contenteditable="true"]'
        );
      },
      /**
       * 이전 — 이미지 모드: 열화상 / 모듈 모드: 같은 모듈 이전 결함
       */
      async navigateToPrevious() {
        if (this.isModuleNavigationMode) {
          await this.$store.dispatch('defect/navigateModuleDefectDialog', 'previous');
        } else {
          await this.navigateToNewDefectDialog('previous');
        }
        this.updateDialogTitle();
      },
      /**
       * 다음 — 이미지 모드: 열화상 / 모듈 모드: 같은 모듈 다음 결함
       */
      async navigateToNext() {
        if (this.isModuleNavigationMode) {
          await this.$store.dispatch('defect/navigateModuleDefectDialog', 'next');
        } else {
          await this.navigateToNewDefectDialog('next');
        }
        this.updateDialogTitle();
      },
      /**
       * 다이얼로그가 열린 채 탐색기·캐러셀 등에서 열화상만 바뀐 경우 — store가 새 이미지 기준으로 결함관리 영역을 다시 로드
       */
      async syncDefectDialogToSelectedImage(thermalImage) {
        if (!thermalImage?.id || !this.dialog) {
          return;
        }
        const dialogThermalId = this.thermalImage?.id;
        if (
          dialogThermalId != null &&
          dialogThermalId !== '' &&
          Number(dialogThermalId) === Number(thermalImage.id)
        ) {
          return;
        }
        await this.$store.dispatch('defect/resetDefectDialogForNavigation', {
          thermalImage,
          isDefectSelected: false,
          showList: this.showList
        });
        this.updateDialogTitle();
      },
      /**
       * Alt + ← : 이전 이미지 ,Alt + → : 다음 이미지  ,Alt + W : 결함 리스트 토글 , Alt + Q : 결함 등록
       */
      handleShortcuts(event) {
        if (!this.dialog) return;

        /* 결함 목록(리스트 모드): document capture에서 일괄 처리 — 예전처럼 ↑↓ 이동 + Enter로 항목 진입 */
        if (this.showList && this.defectList.length > 0 && !event.altKey) {
          if (event.code === 'ArrowDown') {
            event.preventDefault();
            event.stopPropagation();
            this.moveFocus(this.focusedListItemIndex, 1);
            return;
          }
          if (event.code === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();
            this.moveFocus(this.focusedListItemIndex, -1);
            return;
          }
          if (event.code === 'Enter' || event.code === 'NumpadEnter') {
            if (this.isKeyboardInteractiveTarget(event.target)) {
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            const defect = this.defectList[this.focusedListItemIndex];
            if (defect) {
              this.toggleList(defect, false);
            }
            return;
          }
        }

        // Alt + 방향키로 이미지 네비게이션 (하드웨어 키 코드 기준)
        if (event.altKey) {
          if (event.code === 'ArrowLeft' && this.canNavigatePrevious) {
            event.preventDefault();
            this.navigateToPrevious();
          }
          if (event.code === 'ArrowRight' && this.canNavigateNext) {
            event.preventDefault();
            this.navigateToNext();
          }

          // Alt + W로 결함 리스트 토글 (하드웨어 키 코드 기준)
          if (event.code === 'KeyW') {
            event.preventDefault();
            this.toggleList(null, true);
          }

          // Alt + Q로 결함 등록 (하드웨어 키 코드 기준)
          if (event.code === 'KeyQ') {
            event.preventDefault();
            this.toggleList(null, false);
          }
        }
      },
      /**
       * Vue 2: v-for마다 다른 ref 이름(`defect-item-0`)이면 ref는 단일 컴포넌트(배열 아님).
       * 예전에 `ref[0]`만 접근해 포커스가 항상 실패함.
       */
      listItemRefToComponent(ref) {
        if (ref == null) return null;
        return Array.isArray(ref) ? ref[0] : ref;
      },
      listItemRefToEl(ref) {
        const inst = this.listItemRefToComponent(ref);
        if (!inst) return null;
        return inst.$el || inst;
      },
      focusDefectListItemAt(index) {
        if (!this.defectList.length) return;
        const i = Math.max(0, Math.min(index, this.defectList.length - 1));
        this.focusedListItemIndex = i;
        const tryFocus = (attempt = 0) => {
          this.$nextTick(() => {
            const el = this.listItemRefToEl(this.$refs[`defect-item-${i}`]);
            if (el && typeof el.focus === 'function') {
              el.focus();
              el.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
              return;
            }
            if (attempt < 5) {
              setTimeout(() => tryFocus(attempt + 1), 50);
            }
          });
        };
        tryFocus();
      },
      focusFirstListItem() {
        if (this.defectList.length <= 0) return;
        let idx = 0;
        const fromStore =
          this.focusDefectListItemId != null && this.focusDefectListItemId !== ''
            ? Number(this.focusDefectListItemId)
            : null;
        const fromDefect =
          this.defect?.id != null && this.defect.id !== '' ? Number(this.defect.id) : null;
        const targetId =
          fromStore != null && Number.isFinite(fromStore)
            ? fromStore
            : fromDefect != null && Number.isFinite(fromDefect)
              ? fromDefect
              : null;
        if (targetId != null) {
          const found = this.defectList.findIndex(
            d =>
              d &&
              (Number(d.id) === targetId ||
                Number(d.seq) === targetId ||
                Number(d.moduleDefectSeq) === targetId)
          );
          if (found >= 0) idx = found;
        }
        this.focusDefectListItemAt(idx);
      },
      onDefectListRowClick(defect, index) {
        this.focusedListItemIndex = index;
        this.toggleList(defect, false);
      },
      getActionStrategy(defect, showList) {
        const strategies = {
          showDefectList: {
            name: 'showDefectList',
            description: '결함 목록 표시',
            execute: () => this.showDefectList()
          },
          editDefect: {
            name: 'editDefect',
            description: '결함 수정 모드',
            execute: defect => this.editDefect(defect)
          },
          createNewDefect: {
            name: 'createNewDefect',
            description: '새 결함 추가 모드',
            execute: () => this.createNewDefect()
          }
        };

        // 조건부 로직을 함수형으로 처리
        const getStrategy = () => {
          if (showList) return strategies.showDefectList;
          if (defect) return strategies.editDefect;
          return strategies.createNewDefect;
        };

        const strategy = getStrategy();

        // defect 파라미터가 필요한 경우 바인딩
        if (strategy === strategies.editDefect) {
          return {
            ...strategy,
            execute: () => strategy.execute(defect)
          };
        }

        return strategy;
      },
      moveFocus(index, direction) {
        if (this.defectList.length > 0) {
          const newIndex = (index + direction + this.defectList.length) % this.defectList.length;
          this.focusDefectListItemAt(newIndex);
        }
      }
    },
    watch: {
      dialog: {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            this.initializeDialog();
          } else {
            this.removeGlobalEventListeners();
          }
        }
      },
      shouldRenewDefectList(shouldRenew) {
        console.log('[DEFECT INFO DIALOG] shouldRenewDefectList watch 호출:', shouldRenew);
        console.log('[DEFECT INFO DIALOG] thermalImage:', this.thermalImage);

        if (shouldRenew && this.thermalImage) {
          this.initialize(this.thermalImage.id).then(() => {});
          this.$store.dispatch('defect/completeDefectListRenew');
        } else {
          console.log('[DEFECT INFO DIALOG] 결함 목록 갱신 조건 불만족:', {
            shouldRenew,
            hasThermalImage: !!this.thermalImage
          });
        }
      },
      showList(newVal) {
        if (newVal && this.defectList && this.defectList.length > 0) {
          this.$nextTick(() => {
            this.focusFirstListItem();
          });
        }
      },
      defectList(newList) {
        // [DEBUG] defectList 변경 시 status 분포 로깅
        if (newList && newList.length > 0) {
          const statusSet = new Set(newList.map(d => d?.defectStatus));
          console.log(
            `[DEFECT-INFO-DIALOG] defectList updated (n=${newList.length})`,
            'unique defectStatus:',
            [...statusSet],
            'sample:',
            newList.slice(0, 3).map(d => ({ id: d.id ?? d.seq, status: d.defectStatus }))
          );
        }
        if (this.showList && newList && newList.length > 0) {
          this.$nextTick(() => {
            this.focusFirstListItem();
          });
        }
      },
      // thermalImage 변경 시 제목 업데이트
      currentThermalImageId(newVal, oldVal) {
        if (newVal !== oldVal && this.dialog) {
          console.log('[DEFECT INFO DIALOG] thermalImage 변경 감지:', { oldVal, newVal });
          this.updateDialogTitle();
        }
      },
      /** 탐색기에서 다른 행 선택 시 dashboard만 바뀌고 defect.thermalImage는 그대로인 경우 동기화 */
      'selectedThermalImage.id': {
        handler(newId, oldId) {
          if (this.isModuleNavigationMode) {
            return;
          }
          if (!this.dialog || newId == null || newId === '') {
            return;
          }
          if (oldId == null || oldId === '') {
            return;
          }
          if (Number(newId) === Number(oldId)) {
            return;
          }
          this.syncDefectDialogToSelectedImage(this.selectedThermalImage);
        }
      }
    },
    mounted() {
      // EventBus 리스너 제거 - Vuex store로 대체됨
    },
    beforeUnmount() {
      this.removeGlobalEventListeners();
    }
  };
</script>

<style scoped>
  .defect-info-dialog-card {
    overflow: hidden;
    min-height: 0;
    border-color: var(--v-borderSubtle-base) !important;
    background-color: var(--v-surface-base) !important;
  }

  /* 헤더: 제목(키커+타이틀)과 액션 버튼을 세로 중앙 정렬 — 공통 CrudFormDialogHeader는 flex-start라 위에 붙어 보임 */
  .defect-info-dialog-shell ::v-deep .solvit-dialog-form-header__row {
    align-items: center;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

  .defect-info-dialog-shell ::v-deep .solvit-dialog-form-header__append {
    align-items: center;
  }

  /* 결함 등록 / 결함 리스트: 기본(v-btn medium)보다 한 단계 더 눈에 띄게 */
  .defect-info-dialog-shell ::v-deep .defect-info-header-action-btn.v-btn {
    min-height: 40px !important;
    padding-left: 16px !important;
    padding-right: 16px !important;
    font-size: 0.9375rem !important;
    font-weight: 600 !important;
  }

  .defect-info-list-surround {
    border-color: var(--v-borderSubtle-base) !important;
    background-color: color-mix(in srgb, var(--v-surface-base) 98%, transparent) !important;
  }

  .defect-info-defect-list {
    max-height: min(420px, 55vh);
    overflow-y: auto;
  }

  .defect-list-item {
    cursor: pointer;
    transition: background-color 0.2s ease;
    outline: none !important;
  }

  .defect-list-item:hover {
    background-color: color-mix(in srgb, var(--v-text-base) 6%, transparent);
  }

  .defect-list-item:focus {
    background-color: var(--v-primary-base) !important;
    border: 2px solid var(--v-primary-base) !important;
    outline: 2px solid var(--v-primary-base) !important;
    outline-offset: -2px !important;
    color: white !important;
    box-shadow: 0 0 0 2px var(--v-primary-base) !important;
  }

  .defect-list-item:focus .v-list-item__title,
  .defect-list-item:focus .v-list-item__subtitle {
    color: white !important;
  }

  /* 포커스가 없는 상태 명확히 표시 */
  .defect-list-item:not(:focus) {
    background-color: transparent !important;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
  }

  .focused-item {
    background-color: var(--v-primary-base) !important;
    color: white !important;
    border: 2px solid var(--v-primary-base) !important;
    outline: 2px solid var(--v-primary-base) !important;
    outline-offset: -2px !important;
    box-shadow: 0 0 0 2px var(--v-primary-base) !important;
  }

  .focused-item .v-list-item__title,
  .focused-item .v-list-item__subtitle {
    color: white !important;
  }
</style>
